import React, { useState } from 'react';
import { auth, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { showToast } from '../components/Toast';

function ModalLogin({ onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const ensureUserDocumentFunction = async (user) => {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        const userData = {
          email: user.email,
          displayName: user.displayName || 'Usuario',
          photoURL: user.photoURL || null,
          role: 'client',
          createdAt: serverTimestamp(),
        };

        await setDoc(userRef, userData);
        showToast('Profile created automatically', 'info');
      }
    } catch (dbError) {
      console.error('❌ Error en ensureUserDocumentFunction:', dbError);
      
      if (dbError.code === 'permission-denied') {
        showToast('Permission denied: Check Firestore security rules', 'error', true);
      } else if (dbError.code === 'not-found') {
        showToast('The "users" collection does not exist in Firestore', 'error', true);
      } else {
        showToast('Error creating user profile: ' + dbError.message, 'error', true);
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      await ensureUserDocumentFunction(userCredential.user);
      
      showToast('¡Login exitoso!', 'success');
      
      setLoading(false);
      
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      setLoading(false);
      if (error.code === 'auth/user-not-found') {
        setError('Usuario no encontrado');
      } else if (error.code === 'auth/wrong-password') {
        setError('Contraseña incorrecta');
      } else if (error.code === 'auth/invalid-email') {
        setError('Email inválido');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Demasiados intentos fallidos. Intenta más tarde');
      } else {
        setError('Error: ' + (error.message || 'Error desconocido'));
      }
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      provider.setCustomParameters({
        'prompt': 'consent'
      });

      const result = await signInWithPopup(auth, provider);
      
      await ensureUserDocumentFunction(result.user);
      
      showToast('¡Login con Google exitoso!', 'success');
      
      setLoading(false);
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      setLoading(false);
      
      if (error.code === 'auth/operation-not-allowed') {
        setError('Google Sign-in no está habilitado en Firebase');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Error de conexión. Intenta de nuevo');
      } else if (error.code === 'auth/popup-blocked') {
        setError('El popup fue bloqueado. Permite popups en tu navegador');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setError('Este email está registrado con otro método');
      } else if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        setError('Error al iniciar sesión con Google: ' + (error.message || 'Error desconocido'));
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        
        <div className="modal-header">
          <h2>🔑 Login</h2>
          <p>Ingresa a Starboy Records</p>
        </div>

        <form onSubmit={handleLogin} className="modal-form">
          <div className="form-group">
            <label htmlFor="email">📧 Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">🔒 Contraseña</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>

        <div className="divider">
          <span>O</span>
        </div>

        <button 
          type="button" 
          className="google-btn" 
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <img 
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" 
            alt="Google logo" 
            className="google-icon"
          />
          Continuar con Google
        </button>
      </div>
    </div>
  );
}

export default ModalLogin;
